import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Cookies from 'js-cookie';
import { logout, isAuthenticated, tokenStatus } from './auth';
import { isLeder, erMekaniker, kanEndrePriser, lagreTilgang, glemTilgang, jobberI, paPause } from './employeeAccess';
import UbehandledeOrdrer from './components/UbehandledeOrdrer';
import Ikon from './ny-kundesamhandling/Ikon';
import NyeSkiplukk from './components/NyeSkiplukk';
import { erKundeside } from './kundesider';
import { varselLydPa, spillVarselLyd, nyttVarselSidenSist, VARSEL_LYD_ENDRET } from './utils/varselLyd';
import { fmtDT } from './utils/format';

// Kort tekst med … når den er lengre enn maks tegn.
const kortTekst = (t, maks) => { const s = String(t || '').trim(); return s.length > maks ? `${s.slice(0, maks - 1).trimEnd()}…` : s; };

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function NavBar() {
  const [isOpen, setIsOpen] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(Cookies.get('selectedEmployee') || '');
  // Varsel om ubehandlede ordrer når en ansatt logger inn (velger seg selv); n gir ny henting hver gang.
  const [ordreVarsel, setOrdreVarsel] = useState(null);
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState(Cookies.get('selectedStore') || '');
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isStoreModalOpen, setIsStoreModalOpen] = useState(false);
  const [clubOrderCount, setClubOrderCount] = useState(0);
  const [varslingCount, setVarslingCount] = useState(0);
  const [lydPa, setLydPa] = useState(varselLydPa());
  const [nyeNettbutikkOrdre, setNyeNettbutikkOrdre] = useState(0);
  // Siste utførte gjøremål i valgt butikk, vises i den smale linja (ansatt).
  const [sisteGjoremal, setSisteGjoremal] = useState(null);
  const [isEmployeeModalOpen, setIsEmployeeModalOpen] = useState(false);
  const [activeCampaign, setActiveCampaign] = useState(null);
  // Tegner menyen på nytt når tilgang/roller endres (se employeeUpdated under), også når navnet er det samme.
  const [, setTilgangVersjon] = useState(0);


  // Verksted dropdown/accordion
  const [isWorkshopOpen, setIsWorkshopOpen] = useState(false);
  const workshopRef = useRef(null);

  // Nettbutikk dropdown/accordion
  const [isNettbutikkOpen, setIsNettbutikkOpen] = useState(false);
  const nettbutikkRef = useRef(null);

  // Sammenfoldede seksjoner i innstillingsmenyen - lukket til de åpnes
  const [isVerktoyOpen, setIsVerktoyOpen] = useState(false);
  const [isHjelpemidlerOpen, setIsHjelpemidlerOpen] = useState(false);
  const [isKlubbOpen, setIsKlubbOpen] = useState(false);

  const settingsRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Skjul navbar i public-portal
  const path = location.pathname.toLowerCase();
  // Alle kundesidene (src/kundesider.js), også når en ansatt er innlogget i samme nettleser.
  const hide = erKundeside(path) || /^\/trykkeguide\/[^/]+\/print$/.test(path) || /^\/plakater\/[^/]+$/.test(path);

  // Rent oppslag (ingen bivirkning) med vilje - NavBar rendres FØR det
  // rutede innholdet (se App.js), så et isAuthenticated()-kall her ville
  // rukket å fjerne et utløpt token fra localStorage før ProtectedRoute
  // lenger nede fikk lest det, og dermed aldri fått vite AT det var utløpt
  // (vist som "du ble logget ut" på innloggingssiden - se ProtectedRoute.js).
  const loggedIn = tokenStatus() === 'gyldig';



  const handleLogout = () => {

    logout(); // fjerner token

    // Fjern butikk/ansatt-kontekst
    Cookies.remove('selectedStore');
    Cookies.remove('butikkid');
    Cookies.remove('selectedEmployee');
    glemTilgang();
    window.dispatchEvent(new Event('employeeUpdated'));

    setSelectedStore('');

    window.dispatchEvent(new Event("storeUpdated"));
    setSelectedEmployee('');
    setEmployees([]);

    navigate('/login');
  };

  // Åpne modaler ved oppstart (kun hvis innlogget)
  useEffect(() => {
    if (hide || !loggedIn) return;

    const hasStore = !!Cookies.get('selectedStore');
    const hasEmployee = !!Cookies.get('selectedEmployee');

    if (!hasStore) {
      // For sikkerhet – login setter normalt butikk
      setIsStoreModalOpen(true);
    } else if (!hasEmployee) {
      // Butikk er valgt, men ingen ansatt registrert ⇒ spør én gang
      setIsEmployeeModalOpen(true);
    }
  }, [hide, loggedIn, selectedStore]);



  // Hent antall aktive klubbordre
  const fetchClubOrdersCount = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/cluborders/activeorders`);
      if (!response.ok) throw new Error('Feil ved innhenting av aktive ordrer');
      const data = await response.json();
      const butikkIdCookie = Cookies.get('butikkid');
      const matchingOrders = data.orders.filter(order => {
        const butikktilhorighet = order.customerId?.butikktilhorighet || 'Ukjent';
        let butikkIdKlubb = null;
        if (butikktilhorighet === 'ROYKEN') butikkIdKlubb = '1';
        if (butikktilhorighet === 'SLEMMESTAD') butikkIdKlubb = '2';
        const matchButikk = butikkIdKlubb === butikkIdCookie || butikkIdKlubb === null;
        const isAktiv = order.status === 'Aktiv';
        return matchButikk && isAktiv;
      });
      setClubOrderCount(matchingOrders.length);
    } catch (e) {
      console.error('Feil ved henting av antall aktive ClubOrders:', e);
    }
  };

  const fetchVarslingCount = async () => {
    try {
      const r = await fetch(`${API_BASE_URL}/notifications/unread?t=${Date.now()}`, { cache: "no-store" });
      if (!r.ok) return;
      const data = await r.json();
      setVarslingCount(data.totalUnread || 0);
      if (varselLydPa() && nyttVarselSidenSist(data.items)) spillVarselLyd();
    } catch (_) {}
  };

  const fetchNyeNettbutikkOrdre = async () => {
    try {
      const r = await fetch(`${API_BASE_URL}/nettbutikk-ordre/nye?t=${Date.now()}`, { cache: "no-store" });
      if (!r.ok) return;
      const data = await r.json();
      setNyeNettbutikkOrdre(data.antall || 0);
    } catch (_) {}
  };

  const fetchSisteGjoremal = async () => {
    const butikkid = Cookies.get('butikkid');
    if (!butikkid) return setSisteGjoremal(null);
    try {
      const r = await fetch(`${API_BASE_URL}/completedtasks?store=${encodeURIComponent(butikkid)}&limit=1`, { cache: 'no-store' });
      if (!r.ok) return;
      const liste = await r.json();
      // Sorterer også her, så linja er riktig før backend med ?limit er deployet.
      const siste = (Array.isArray(liste) ? liste : [])
        .reduce((a, b) => (!a || new Date(b.dateCompleted) > new Date(a.dateCompleted) ? b : a), null);
      setSisteGjoremal(siste);
    } catch (_) {}
  };

  // Hentes ved navigasjon, butikkbytte, når et gjøremål blir utført og hvert 5. minutt.
  useEffect(() => {
    if (hide || !isAuthenticated()) return;
    fetchSisteGjoremal();
    const onUtfort = () => fetchSisteGjoremal();
    window.addEventListener('gjoremalUtfort', onUtfort);
    const id = setInterval(() => { if (isAuthenticated()) fetchSisteGjoremal(); }, 5 * 60 * 1000);
    return () => { window.removeEventListener('gjoremalUtfort', onUtfort); clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, hide, selectedStore]);

  // Oppdater teller ved navigasjon. Tellerne krever innlogging, så de hentes
  // ikke på innloggingssiden (ga ellers en strøm av 401-kall i loggen).
  useEffect(() => {
    if (hide || !isAuthenticated()) return;
    fetchClubOrdersCount();
    fetchVarslingCount();
    fetchNyeNettbutikkOrdre();
  }, [location, hide]);

  // Oppdater varsling-teller hvert 5. minutt i bakgrunnen
  useEffect(() => {
    if (hide) return;
    const id = setInterval(() => {
      if (isAuthenticated()) {
        fetchVarslingCount();
        fetchNyeNettbutikkOrdre();
      }
    }, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [hide]);

  // Med varsellyd på sjekkes varslene hvert minutt, så lyden kommer kort tid
  // etter at kunden har skrevet (bakgrunnsfaner sjekkes også, omtrent like ofte).
  useEffect(() => {
    const onEndret = () => setLydPa(varselLydPa());
    window.addEventListener(VARSEL_LYD_ENDRET, onEndret);
    window.addEventListener('storage', onEndret);
    return () => {
      window.removeEventListener(VARSEL_LYD_ENDRET, onEndret);
      window.removeEventListener('storage', onEndret);
    };
  }, []);

  useEffect(() => {
    if (hide || !lydPa) return;
    const id = setInterval(() => {
      if (isAuthenticated()) fetchVarslingCount();
    }, 60 * 1000);
    return () => clearInterval(id);
  }, [hide, lydPa]);

  // Salgsrapporten har markert nye ordre som sett
  useEffect(() => {
    // Tallet er ordrer som venter på behandling, så det hentes på nytt i stedet
    // for å nullstilles når salgsrapporten åpnes eller en ordre behandles.
    const onSett = () => fetchNyeNettbutikkOrdre();
    window.addEventListener('nettbutikkOrdreSett', onSett);
    return () => window.removeEventListener('nettbutikkOrdreSett', onSett);
  }, []);

  // Lytt på employeeUpdated (ansatt kan byttes andre steder, f.eks. i dialogen på en service)
  useEffect(() => {
    const handleEmployeeUpdate = () => {
      setSelectedEmployee(Cookies.get('selectedEmployee') || '');
      setTilgangVersjon((v) => v + 1);
    };
    window.addEventListener('employeeUpdated', handleEmployeeUpdate);
    return () => window.removeEventListener('employeeUpdated', handleEmployeeUpdate);
  }, []);

  // Lytt på storeUpdated
  useEffect(() => {
    if (hide) return;
    const handleStoreUpdate = () => {
      setSelectedStore(Cookies.get('selectedStore') || '');
    };
    window.addEventListener('storeUpdated', handleStoreUpdate);
    return () => window.removeEventListener('storeUpdated', handleStoreUpdate);
  }, [hide]);

  // Hent ansatte og butikker
  useEffect(() => {
    if (hide || !loggedIn) return;

    const fetchEmployees = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/employees`);
        const data = await response.json();
        const selectedStoreName = Cookies.get('selectedStore');
        const filtered = data.filter((emp) => jobberI(emp, selectedStoreName));
        setEmployees(filtered);

        // Tilgang og roller hentes på nytt ved hver lasting, så en endring under Ansatte (f.eks. ny
        // rolle) gjelder uten å velge ansatt på nytt. Retter også økter fra før tilgangsnivå ble
        // sporet (§38), der bare selectedEmployee-cookien var satt.
        const currentSelected = Cookies.get('selectedEmployee');
        const match = currentSelected && filtered.find((emp) => emp.navn === currentSelected);
        if (match && lagreTilgang(match)) window.dispatchEvent(new Event('employeeUpdated'));

      } catch (e) {
        console.error('Error fetching employees:', e);
      }
    };
    const fetchStores = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/stores`);
        const data = await response.json();
        setStores(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error('Error fetching stores:', e);
      }
    };
    fetchEmployees();
    fetchStores();
  }, [hide, loggedIn, selectedStore]);


  // Klikk utenfor settings
  useEffect(() => {
    if (hide) return;
    const handleClickOutside = (event) => {
      if (settingsRef.current && !settingsRef.current.contains(event.target)) {
        setIsSettingsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [hide]);

  // Klikk utenfor Verksted-dropdown
  useEffect(() => {
    const onDocClick = (e) => {
      // På mobil (< md) skal ikke desktop "klikk utenfor" styre Verksted
      if (window.innerWidth < 768) return;

      if (!workshopRef.current) return;
      if (!workshopRef.current.contains(e.target)) {
        setIsWorkshopOpen(false);
      }
    };

    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  // Klikk utenfor Nettbutikk-dropdown
  useEffect(() => {
    const onDocClick = (e) => {
      // På mobil (< md) skal ikke desktop "klikk utenfor" styre Nettbutikk
      if (window.innerWidth < 768) return;

      if (!nettbutikkRef.current) return;
      if (!nettbutikkRef.current.contains(e.target)) {
        setIsNettbutikkOpen(false);
      }
    };

    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  // Lukk dropdowns med Escape
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setIsWorkshopOpen(false);
        setIsNettbutikkOpen(false);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // Sync cookies -> state
  useEffect(() => {
    if (hide) return;
    const storeFromCookie = Cookies.get('selectedStore');
    const storeIdFromCookie = Cookies.get('butikkid');
    if (storeFromCookie && storeFromCookie !== selectedStore) {
      setSelectedStore(storeFromCookie);
    }
    if (storeIdFromCookie) {
      console.log(`Butikk-ID fra cookie er ${storeIdFromCookie}`);
    }
  }, [selectedStore, hide]);

  // ---------------- AKTIV KAMPANJE-BANNER ----------------
  useEffect(() => {
    if (hide || !loggedIn) return;

    async function fetchCampaign() {
      try {
        const res = await fetch(`${API_BASE_URL}/events`);
        if (!res.ok) return;

        const events = await res.json();
        const now = new Date();

        // Finn kampanjer
        const active = events.find(ev => {
          if (ev.category !== "KAMPANJE") return false;
          if (!ev.start || !ev.end) return false;

          const start = new Date(ev.start);
          const end = new Date(ev.end);

          return now >= start && now <= end;
        });

        setActiveCampaign(active || null);
      } catch (err) {
        console.error("Feil ved henting av kampanjer:", err);
      }
    }

    fetchCampaign();
  }, [location, hide, loggedIn]);


  // ---------- HANDLERS SOM JSX REFERERER TIL ----------
  const handleNavigation = (e, to) => {
    if (e) e.preventDefault();

    if (!selectedEmployee) {
      setIsEmployeeModalOpen(true);
      return;
    }

    // Lukk mobilmeny + verksted-accordion når vi navigerer
    setIsOpen(false);
    setIsWorkshopOpen(false);

    // Bruk react-router navigering
    navigate(to);
  };


  // Serviceliste er den mest brukte undersiden, så vi åpner den automatisk
  // når Verksted-dropdownen/accordionen åpnes.
  const toggleWorkshopMenu = () => {
    const opening = !isWorkshopOpen;

    if (opening && !selectedEmployee) {
      setIsEmployeeModalOpen(true);
      return;
    }

    setIsWorkshopOpen(opening);
    if (opening) {
      // setIsOpen(false) ville lukket hele mobilmenyen her — ikke bare
      // Verksted-accordionen — så vi lar hovedmenyen stå åpen og bare navigerer.
      navigate('/workshop');
    }
  };

  const openStoreModal = () => setIsStoreModalOpen(true);

  const handleEmployeeChange = (event) => {
    const selected = event.target.value;
    setSelectedEmployee(selected);
    Cookies.set('selectedEmployee', selected);
    // Tilgangsnivå ("ansatt"/"leder") og roller lagres sammen med navnet, slik at sider kan
    // sjekke isLeder()/erMekaniker() uten et eget API-kall — se employeeAccess.js (§38).
    lagreTilgang(employees.find((emp) => emp.navn === selected));
    window.dispatchEvent(new Event('employeeUpdated'));
    setIsEmployeeModalOpen(false);
    if (selected) setOrdreVarsel((v) => ({ navn: selected, n: (v?.n || 0) + 1 }));
  };

  const toggleMenu = () => setIsOpen((v) => !v);
  const toggleSettingsMenu = () => setIsSettingsOpen((v) => !v);

  const handleOutsideClick = (event) => {
    if (event.target.classList.contains('modal-background')) {
      setIsStoreModalOpen(false);
      setIsEmployeeModalOpen(false);
    }
  };



  const handleStoreChange = (event) => {
    const selectedStoreName = event.target.value;
    const selectedStoreObject = stores.find((store) => store.butikknavn === selectedStoreName);
    if (selectedStoreObject) {
Cookies.set('selectedStore', selectedStoreObject.butikknavn);
Cookies.set('butikkid', selectedStoreObject.butikkid);
setSelectedStore(selectedStoreObject.butikknavn);

window.dispatchEvent(new Event("storeUpdated"));

      

      // Nullstill valgt ansatt når butikk endres
      Cookies.remove('selectedEmployee');
      glemTilgang();
      setSelectedEmployee('');
      window.dispatchEvent(new Event('employeeUpdated'));

      // Åpne ansatt-modal igjen for ny butikk
      setIsEmployeeModalOpen(true);
    }
    setIsStoreModalOpen(false);
  };

  // ----------------------------------------------------

  // Ikke vis navbar hvis vi er på public-portal (/svc/...)
  // eller hvis brukeren ikke er innlogget (mangler token)
  if (hide || !loggedIn) return null;



  // Leder-funksjoner (priser, oppsett, rapporter, nettbutikk) vises bare når valgt ansatt er
  // leder. Bare et skille i menyen, ikke tilgangskontroll — se employeeAccess.js og ny-kundesamhandling-plan.md.
  const leder = isLeder();
  // Menylinjen bare for leder og mekaniker (vinnsel, 30.10). Andre, også før en ansatt er valgt,
  // får en smal linje med Kontrollpanel, butikk, ansatt og Logg ut; resten nås fra Kontrollpanel.
  // Bare et skille i menyen, ikke tilgangskontroll: sidene og backend er låst som før.
  // Leses fra cookien ved hver tegning, så menyen følger valgt ansatt med en gang.
  const fullMeny = leder || erMekaniker();

  const chevron = (open) => (
    <svg className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} viewBox="0 0 20 20" fill="currentColor">
      <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z" clipRule="evenodd" />
    </svg>
  );

  const navLink = 'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-slate-300 hover:text-white hover:bg-white/10 text-sm font-medium transition-colors';
  const dropdownItem = 'block w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors';

  // Sammenfoldet seksjon i innstillingsmenyen: overskrift som knapp, lenkene vises når den er åpen.
  const settingsSeksjon = (label, open, setOpen, items) => (
    <>
      <li className='border-t border-gray-100 mt-1 pt-1'>
        <button
          type='button'
          onClick={() => setOpen(v => !v)}
          aria-expanded={open}
          className={`${dropdownItem} flex items-center justify-between`}
        >
          {label} {chevron(open)}
        </button>
      </li>
      {open && items.map(({ to, label: itemLabel, onClick }) => (
        <li key={to || itemLabel}>
          {to
            ? <Link to={to} onClick={toggleSettingsMenu} className={`${dropdownItem} pl-8`}>{itemLabel}</Link>
            : <button type='button' onClick={onClick} className={`${dropdownItem} pl-8`}>{itemLabel}</button>}
        </li>
      ))}
    </>
  );

  const modaler = (
    <>
        {/* Butikkvalg-modal */}
        {isStoreModalOpen && (
          <div className='modal-background fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50' onClick={handleOutsideClick}>
            <div className='bg-white p-6 rounded-2xl shadow-2xl'>
              <h2 className='text-lg font-semibold text-gray-900 mb-4'>Velg butikk</h2>
              <select aria-label='Velg butikk' value={selectedStore} onChange={handleStoreChange} className='w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500'>
                <option value=''>Velg butikk</option>
                {stores.map((store) => (
                  <option key={store.butikkid} value={store.butikknavn}>{store.butikknavn}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* Ansattvalg-modal */}
        {isEmployeeModalOpen && (
          <div className='modal-background fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50' onClick={handleOutsideClick}>
            <div className='bg-white p-6 rounded-2xl shadow-2xl'>
              <h2 className='text-lg font-semibold text-gray-900 mb-4'>Velg ansatt</h2>
              <select aria-label='Velg ansatt' value={selectedEmployee} onChange={handleEmployeeChange} className='w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500'>
                <option value=''>Velg ansatt</option>
                {employees.map((employee) => (
                  <option key={employee._id} value={employee.navn} className={paPause(employee) ? 'text-slate-500' : undefined}>{employee.navn}{paPause(employee) ? ' (pause)' : ''}</option>
                ))}
              </select>
            </div>
          </div>
        )}
    </>
  );

  // Etter menyen i begge visningene: kampanjebanner og varslene når en ansatt velges.
  const etterMeny = (
    <>
      {/* Kampanjebanner */}
      {activeCampaign && (
        <div
          onClick={() => navigate('/eventcalendar')}
          role='button'
          className='no-print bg-amber-400 text-amber-900 text-center py-2 px-4 text-sm font-semibold flex items-center justify-center gap-2 cursor-pointer hover:bg-amber-300 transition-colors'
        >
          <span>🎉 Aktiv kampanje:</span>
          <strong>{activeCampaign.title}</strong>
          <span className='text-amber-700'>
            {new Date(activeCampaign.start).toLocaleDateString('no-NO')}
            {' – '}
            {new Date(activeCampaign.end).toLocaleDateString('no-NO')}
          </span>
        </div>
      )}

      {/* Ved innlogging: først nye skiplukk den ansatte skal følge opp, så ubehandlede ordrer. */}
      {ordreVarsel && !ordreVarsel.skiplukkFerdig && (
        <NyeSkiplukk
          key={`s${ordreVarsel.n}`}
          ansatt={ordreVarsel.navn}
          onFerdig={() => setOrdreVarsel((v) => (v ? { ...v, skiplukkFerdig: true } : v))}
        />
      )}
      {ordreVarsel?.skiplukkFerdig && (
        <UbehandledeOrdrer key={ordreVarsel.n} ansatt={ordreVarsel.navn} onLukk={() => setOrdreVarsel(null)} />
      )}
    </>
  );

  // Venstre del i begge visningene: Kontrollpanel, butikk og ansatt (vinnsel, 30.10). mork = på
  // den svarte menylinjen til leder og mekaniker.
  const venstre = (mork = false) => (
    <div className='flex min-w-0 flex-wrap items-center gap-2'>
      <button
        type='button'
        onClick={(e) => handleNavigation(e, '/')}
        className='inline-flex min-h-[2.25rem] shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg bg-indigo-600 px-3 text-sm font-semibold text-white hover:bg-indigo-500 focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50'
      >
        <Ikon navn='hjem' className='w-4 h-4' />
        Kontrollpanel
      </button>
      <button
        type='button'
        onClick={openStoreModal}
        title='Bytt butikk'
        className={`inline-flex min-h-[2.25rem] min-w-0 items-center gap-1.5 rounded-lg border px-2.5 text-sm font-medium focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50 ${mork ? 'border-slate-600 bg-slate-800 text-slate-200 hover:bg-slate-700' : 'border-slate-300 bg-white text-slate-800 hover:bg-slate-50'}`}
      >
        <span aria-hidden='true' className='h-2 w-2 shrink-0 rounded-full bg-emerald-500' />
        <span className='truncate'>{selectedStore || 'Velg butikk'}</span>
        <span className='sr-only'>, bytt butikk</span>
      </button>
      <select
        aria-label='Velg ansatt'
        value={selectedEmployee}
        onChange={handleEmployeeChange}
        disabled={isStoreModalOpen || isEmployeeModalOpen}
        className={`min-h-[2.25rem] min-w-0 max-w-[12rem] rounded-lg border px-2 text-sm focus:border-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-600/30 ${mork ? 'border-slate-600 bg-slate-800 text-slate-200' : 'border-slate-300 bg-white text-slate-900'}`}
      >
        <option value=''>Velg ansatt</option>
        {employees.map((employee) => (
          <option key={employee._id} value={employee.navn} className={paPause(employee) ? 'text-slate-500' : undefined}>{employee.navn}{paPause(employee) ? ' (pause)' : ''}</option>
        ))}
      </select>
    </div>
  );

  if (!fullMeny) {
    return (
      <>
        <div id='topLink' />
        <nav aria-label='Hovedmeny' className='no-print border-b border-slate-200 bg-white'>
          <div className='mx-auto flex max-w-7xl flex-wrap items-center gap-2 px-2 py-1.5 sm:px-4'>
            {venstre()}
            {sisteGjoremal && (
              // «Siste: oppgave – navn, dato». Oppgaveteksten kortes til 40 tegn, og hele linja kuttes med
              // … på smale skjermer, så den ikke gjør siden bredere. Hele teksten står i title.
              <Link
                to='/todo'
                onClick={(e) => handleNavigation(e, '/todo')}
                title={`Siste utførte gjøremål: ${sisteGjoremal.task} – ${sisteGjoremal.employee}, ${fmtDT(sisteGjoremal.dateCompleted)}`}
                className='inline-flex min-h-[2.25rem] min-w-0 max-w-full flex-1 basis-56 items-center gap-1.5 rounded-lg px-2 text-sm text-slate-700 hover:bg-slate-50 focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50 sm:max-w-[36rem]'
              >
                <Ikon navn='avkrysning' className='w-4 h-4 shrink-0 text-emerald-600' />
                <span className='shrink-0 font-semibold'>Siste:</span>
                <span className='min-w-0 truncate'>
                  {kortTekst(sisteGjoremal.task, 40)}{' '}
                  <span className='text-slate-600'>– {sisteGjoremal.employee}, {fmtDT(sisteGjoremal.dateCompleted)}</span>
                </span>
              </Link>
            )}
            <button
              type='button'
              onClick={handleLogout}
              className='ml-auto inline-flex min-h-[2.25rem] items-center rounded-lg px-2.5 text-sm font-semibold text-red-700 hover:bg-red-50 focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50'
            >
              Logg ut
            </button>
          </div>
          {modaler}
        </nav>
        {etterMeny}
      </>
    );
  }

  return (
    <>
      <div id='topLink' />
      <nav aria-label='Hovedmeny' className='no-print bg-slate-900 border-b border-slate-700/60'>
        <div className='mx-auto flex max-w-7xl flex-wrap items-center gap-2 px-2 py-1.5 sm:px-4'>

          {venstre(true)}

          {/* Desktop nav */}
          <div className='ml-auto hidden lg:flex items-center gap-0.5'>
            <Link to='/ordre' onClick={(e) => handleNavigation(e, '/ordre')} className={navLink}>Ordre</Link>

            <Link to='/service' onClick={(e) => handleNavigation(e, '/service')} className={navLink}>
              Trykk
              {clubOrderCount > 0 && (
                <span className='bg-blue-500 text-white text-xs font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1'>
                  {clubOrderCount}
                </span>
              )}
            </Link>

            <Link to='/todo' onClick={(e) => handleNavigation(e, '/todo')} className={navLink}>ToDo</Link>

            {/* Verksted dropdown */}
            <div className='relative' ref={workshopRef}>
              <button
                type='button'
                onClick={toggleWorkshopMenu}
                className={navLink}
                aria-haspopup='menu'
                aria-expanded={isWorkshopOpen}
              >
                Verksted
                {varslingCount > 0 && (
                  <span className='bg-red-500 text-white text-xs font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1' aria-label={`${varslingCount} uleste varsler`}>
                    {varslingCount}
                  </span>
                )}
                {chevron(isWorkshopOpen)}
              </button>
              {isWorkshopOpen && (
                <ul role='menu' className='absolute left-0 top-full mt-1.5 w-44 bg-white rounded-xl shadow-xl border border-gray-100 py-1.5 z-50'>
                  <li>
                    <Link role='menuitem' to='/varsling' onClick={(e) => { handleNavigation(e, '/varsling'); setIsWorkshopOpen(false); }} className={`${dropdownItem} flex items-center justify-between gap-2`}>
                      Varsling
                      {varslingCount > 0 && (
                        <span className='bg-red-500 text-white text-xs font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1'>
                          {varslingCount}
                        </span>
                      )}
                    </Link>
                  </li>
                  <li><Link role='menuitem' to='/bookingbike' onClick={(e) => { handleNavigation(e, '/bookingbike'); setIsWorkshopOpen(false); }} className={dropdownItem}>Mottak</Link></li>
                  <li><Link role='menuitem' to='/workshop' onClick={(e) => { handleNavigation(e, '/workshop'); setIsWorkshopOpen(false); }} className={`${dropdownItem} font-semibold text-indigo-600 bg-indigo-50/60`}>Serviceliste</Link></li>
                  {leder && <li><Link role='menuitem' to='/workshop-statistikk' onClick={(e) => { handleNavigation(e, '/workshop-statistikk'); setIsWorkshopOpen(false); }} className={dropdownItem}>Statistikk</Link></li>}
                  <li><Link role='menuitem' to='/reklamasjon-sykkel' onClick={(e) => { handleNavigation(e, '/reklamasjon-sykkel'); setIsWorkshopOpen(false); }} className={dropdownItem}>Reklamasjon sykkel</Link></li>
                  <li><Link role='menuitem' to='/sykkelkort' onClick={(e) => { handleNavigation(e, '/sykkelkort'); setIsWorkshopOpen(false); }} className={dropdownItem}>Alle sykkelkort</Link></li>
                  <li><Link role='menuitem' to='/dekkhotell' onClick={(e) => { handleNavigation(e, '/dekkhotell'); setIsWorkshopOpen(false); }} className={dropdownItem}>Dekkhotell</Link></li>
                  <li><Link role='menuitem' to='/sykkelhotell' onClick={(e) => { handleNavigation(e, '/sykkelhotell'); setIsWorkshopOpen(false); }} className={dropdownItem}>Sykkelhotell</Link></li>
                  <li><Link role='menuitem' to='/skoyteslip' onClick={(e) => { handleNavigation(e, '/skoyteslip'); setIsWorkshopOpen(false); }} className={dropdownItem}>Skøyteslip</Link></li>
                  <li><Link role='menuitem' to='/sjekk-kapasitet' onClick={(e) => { handleNavigation(e, '/sjekk-kapasitet'); setIsWorkshopOpen(false); }} className={dropdownItem}>Sjekk kapasitet</Link></li>
                  {leder && <li><Link role='menuitem' to='/regnskap/sperrede' onClick={(e) => { handleNavigation(e, '/regnskap/sperrede'); setIsWorkshopOpen(false); }} className={dropdownItem}>Sperrede poster</Link></li>}
                </ul>
              )}
            </div>

            {/* Nettbutikk dropdown (leder) */}
            {leder && (
            <div className='relative' ref={nettbutikkRef}>
              <button
                type='button'
                onClick={() => setIsNettbutikkOpen(v => !v)}
                className={navLink}
                aria-haspopup='menu'
                aria-expanded={isNettbutikkOpen}
              >
                Nettbutikk
                {nyeNettbutikkOrdre > 0 && (
                  <span className='bg-red-500 text-white text-xs font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1' aria-label={`${nyeNettbutikkOrdre} nettbutikkordre venter`}>
                    {nyeNettbutikkOrdre}
                  </span>
                )}
                {chevron(isNettbutikkOpen)}
              </button>
              {isNettbutikkOpen && (
                <ul role='menu' className='absolute left-0 top-full mt-1.5 w-52 bg-white rounded-xl shadow-xl border border-gray-100 py-1.5 z-50'>
                  <li><Link role='menuitem' to='/varelager' onClick={(e) => { handleNavigation(e, '/varelager'); setIsNettbutikkOpen(false); }} className={dropdownItem}>Nettbutikk-import</Link></li>
                  <li><Link role='menuitem' to='/produkter' onClick={(e) => { handleNavigation(e, '/produkter'); setIsNettbutikkOpen(false); }} className={dropdownItem}>Produkter</Link></li>
                  <li><Link role='menuitem' to='/kategorier' onClick={(e) => { handleNavigation(e, '/kategorier'); setIsNettbutikkOpen(false); }} className={dropdownItem}>Kategorier</Link></li>
                  <li>
                    <Link role='menuitem' to='/nettbutikk-salgsrapport' onClick={(e) => { handleNavigation(e, '/nettbutikk-salgsrapport'); setIsNettbutikkOpen(false); }} className={`${dropdownItem} flex items-center justify-between gap-2`}>
                      Salgsrapport
                      {nyeNettbutikkOrdre > 0 && (
                        <span className='bg-red-500 text-white text-xs font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1'>
                          {nyeNettbutikkOrdre}
                        </span>
                      )}
                    </Link>
                  </li>
                </ul>
              )}
            </div>
            )}
          </div>

          {/* Høyre: innstillinger og hamburger (mobil) */}
          <div className='ml-auto flex items-center gap-1 lg:ml-0'>
            {/* Innstillinger */}
            <div className='relative' ref={settingsRef}>
              <button
                onClick={toggleSettingsMenu}
                className='p-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition-colors'
                title='Innstillinger'
                aria-label='Innstillinger'
                aria-expanded={isSettingsOpen}
              >
                <svg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24' strokeWidth='2' stroke='currentColor' fill='none' strokeLinecap='round' strokeLinejoin='round'>
                  <path stroke='none' d='M0 0h24v24H0z' />
                  <path d='M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z' />
                  <path d='M9 12a3 3 0 106 0 3 3 0 00-6 0' />
                </svg>
              </button>
              {isSettingsOpen && (
                <ul className='absolute right-0 top-full mt-1.5 w-52 max-h-[80vh] overflow-y-auto bg-white rounded-xl shadow-xl border border-gray-100 py-1.5 z-50'>
                  {[
                    { to: '/eventcalendar', label: 'Eventkalender' },
                  ].map(({ to, label }) => (
                    <li key={to}>
                      <Link to={to} onClick={toggleSettingsMenu} className={dropdownItem}>{label}</Link>
                    </li>
                  ))}
                  {settingsSeksjon('Verktøy', isVerktoyOpen, setIsVerktoyOpen, [
                    { to: '/sendsms', label: 'Send SMS' },
                    ...(leder ? [{ to: '/employees', label: 'Ansatte' }] : []),
                    // Leder og mekaniker (planer/hygraph-plan.md); erstattet «Fastpris arbeid».
                    ...(kanEndrePriser() ? [{ to: '/tjenester', label: 'Tjenester og priser' }] : []),
                    // Plassene i dekk- og sykkelhotellet, også bare leder og mekaniker (Anders, 30.10).
                    ...(kanEndrePriser() ? [{ to: '/hotellkapasitet', label: 'Hotellkapasitet' }] : []),
                    ...(leder ? [{ to: '/sms-templates', label: 'SMS-maler' }] : []),
                  ])}
                  {settingsSeksjon('Hjelpemidler', isHjelpemidlerOpen, setIsHjelpemidlerOpen, [
                    { to: '/offer-list', label: 'Pristilbud' },
                    { to: '/trykkeguide', label: 'Trykkeguide' },
                    { to: '/avtaler', label: 'Avtaleoversikt' },
                    { to: '/klubber', label: 'Klubber' },
                    { to: '/hjelpemidler', label: 'Kalkylekalkulator' },
                    ...(leder ? [
                      { to: '/plakater', label: 'Plakater' },
                      { label: 'Lucky Draw 🎲', onClick: () => window.open('/luckydraw', '_blank', 'width=600,height=600,top=100,left=100,menubar=no,toolbar=no,location=no,status=no') },
                    ] : []),
                  ])}
                  {/* Klubb - ikke i bruk ennå, derfor ikke egen fane i navbar */}
                  {leder && settingsSeksjon('Klubb', isKlubbOpen, setIsKlubbOpen, [
                    { to: '/klubb/fakturaimport', label: 'Fakturaimport' },
                    { to: '/klubb/lager', label: 'Lager' },
                    { to: '/klubb/trenere', label: 'Trenere' },
                    { to: '/klubb/innkjopsansvarlige', label: 'Innkjøpsansvarlige' },
                    { to: '/klubb/roller', label: 'Roller' },
                    { to: '/klubb/bestillinger', label: 'Bestillinger' },
                    { to: '/klubb/produkter', label: 'Vareliste' },
                    { to: '/lager', label: 'Varelager' },
                  ])}
                  <li className='border-t border-gray-100 mt-1 pt-1'>
                    <button onClick={openStoreModal} className={dropdownItem}>Bytt butikk</button>
                  </li>
                  <li>
                    <button onClick={handleLogout} className={`${dropdownItem} text-red-600 hover:bg-red-50 hover:text-red-700`}>Logg ut</button>
                  </li>
                </ul>
              )}
            </div>

            {/* Hamburger (mobil) */}
            <button
              onClick={toggleMenu}
              aria-label={isOpen ? 'Lukk meny' : 'Åpne meny'}
              aria-expanded={isOpen}
              className='lg:hidden shrink-0 p-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition-colors'
            >
              {isOpen ? (
                <svg xmlns='http://www.w3.org/2000/svg' className='h-5 w-5' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth='2'>
                  <path strokeLinecap='round' strokeLinejoin='round' d='M6 18L18 6M6 6l12 12' />
                </svg>
              ) : (
                <svg xmlns='http://www.w3.org/2000/svg' className='h-5 w-5' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth='2'>
                  <path strokeLinecap='round' strokeLinejoin='round' d='M4 6h16M4 12h16M4 18h16' />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Mobilmeny */}
        {isOpen && (
          <div className='lg:hidden border-t border-slate-700/60 bg-slate-900 px-3 py-2 space-y-0.5'>
            <Link to='/ordre' onClick={(e) => handleNavigation(e, '/ordre')} className='block px-3 py-2 rounded-lg text-slate-300 hover:text-white hover:bg-white/10 text-sm font-medium transition-colors'>Ordre</Link>
            <Link to='/todo' onClick={(e) => handleNavigation(e, '/todo')} className='block px-3 py-2 rounded-lg text-slate-300 hover:text-white hover:bg-white/10 text-sm font-medium transition-colors'>ToDo</Link>
            <Link to='/service' onClick={(e) => handleNavigation(e, '/service')} className='flex items-center gap-2 px-3 py-2 rounded-lg text-slate-300 hover:text-white hover:bg-white/10 text-sm font-medium transition-colors'>
              Trykk
              {clubOrderCount > 0 && <span className='bg-blue-500 text-white text-xs font-bold rounded-full px-1.5 py-0.5'>{clubOrderCount}</span>}
            </Link>

            {/* Verksted accordion */}
            <div className='border-t border-slate-700/60 pt-1 mt-1'>
              <button
                onClick={toggleWorkshopMenu}
                className='w-full flex items-center justify-between px-3 py-2 rounded-lg text-slate-300 hover:text-white hover:bg-white/10 text-sm font-medium transition-colors'
              >
                <span className='flex items-center gap-2'>
                  Verksted
                  {varslingCount > 0 && <span className='bg-red-500 text-white text-xs font-bold rounded-full px-1.5 py-0.5' aria-label={`${varslingCount} uleste varsler`}>{varslingCount}</span>}
                </span>
                {chevron(isWorkshopOpen)}
              </button>
              {isWorkshopOpen && (
                <div className='ml-3 mt-0.5 space-y-0.5'>
                  <Link to='/varsling' onClick={(e) => handleNavigation(e, '/varsling')} className='flex items-center gap-2 px-3 py-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 text-sm transition-colors'>
                    Varsling
                    {varslingCount > 0 && <span className='bg-red-500 text-white text-xs font-bold rounded-full px-1.5 py-0.5'>{varslingCount}</span>}
                  </Link>
                  <Link to='/bookingbike' onClick={(e) => handleNavigation(e, '/bookingbike')} className='block px-3 py-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 text-sm transition-colors'>Mottak</Link>
                  <Link to='/workshop' onClick={(e) => handleNavigation(e, '/workshop')} className='block px-3 py-2 rounded-lg text-indigo-300 hover:text-white hover:bg-white/10 text-sm font-semibold transition-colors'>Serviceliste</Link>
                  {leder && <Link to='/workshop-statistikk' onClick={(e) => handleNavigation(e, '/workshop-statistikk')} className='block px-3 py-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 text-sm transition-colors'>Statistikk</Link>}
                  <Link to='/reklamasjon-sykkel' onClick={(e) => handleNavigation(e, '/reklamasjon-sykkel')} className='block px-3 py-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 text-sm transition-colors'>Reklamasjon sykkel</Link>
                  <Link to='/sykkelkort' onClick={(e) => handleNavigation(e, '/sykkelkort')} className='block px-3 py-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 text-sm transition-colors'>Alle sykkelkort</Link>
                  <Link to='/dekkhotell' onClick={(e) => handleNavigation(e, '/dekkhotell')} className='block px-3 py-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 text-sm transition-colors'>Dekkhotell</Link>
                  <Link to='/sykkelhotell' onClick={(e) => handleNavigation(e, '/sykkelhotell')} className='block px-3 py-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 text-sm transition-colors'>Sykkelhotell</Link>
                  <Link to='/skoyteslip' onClick={(e) => handleNavigation(e, '/skoyteslip')} className='block px-3 py-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 text-sm transition-colors'>Skøyteslip</Link>
                  <Link to='/sjekk-kapasitet' onClick={(e) => handleNavigation(e, '/sjekk-kapasitet')} className='block px-3 py-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 text-sm transition-colors'>Sjekk kapasitet</Link>
                  {leder && <Link to='/regnskap/sperrede' onClick={(e) => handleNavigation(e, '/regnskap/sperrede')} className='block px-3 py-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 text-sm transition-colors'>Sperrede poster</Link>}
                </div>
              )}
            </div>

            {/* Nettbutikk accordion (leder) */}
            {leder && (
            <div className='border-t border-slate-700/60 pt-1 mt-1'>
              <button
                onClick={() => setIsNettbutikkOpen(v => !v)}
                className='w-full flex items-center justify-between px-3 py-2 rounded-lg text-slate-300 hover:text-white hover:bg-white/10 text-sm font-medium transition-colors'
              >
                <span className='flex items-center gap-2'>
                  Nettbutikk
                  {nyeNettbutikkOrdre > 0 && <span className='bg-red-500 text-white text-xs font-bold rounded-full px-1.5 py-0.5' aria-label={`${nyeNettbutikkOrdre} nettbutikkordre venter`}>{nyeNettbutikkOrdre}</span>}
                </span>
                {chevron(isNettbutikkOpen)}
              </button>
              {isNettbutikkOpen && (
                <div className='ml-3 mt-0.5 space-y-0.5'>
                  <Link to='/varelager' onClick={(e) => handleNavigation(e, '/varelager')} className='block px-3 py-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 text-sm transition-colors'>Nettbutikk-import</Link>
                  <Link to='/produkter' onClick={(e) => handleNavigation(e, '/produkter')} className='block px-3 py-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 text-sm transition-colors'>Produkter</Link>
                  <Link to='/kategorier' onClick={(e) => handleNavigation(e, '/kategorier')} className='block px-3 py-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 text-sm transition-colors'>Kategorier</Link>
                  <Link to='/nettbutikk-salgsrapport' onClick={(e) => handleNavigation(e, '/nettbutikk-salgsrapport')} className='flex items-center gap-2 px-3 py-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 text-sm transition-colors'>
                    Salgsrapport
                    {nyeNettbutikkOrdre > 0 && <span className='bg-red-500 text-white text-xs font-bold rounded-full px-1.5 py-0.5'>{nyeNettbutikkOrdre}</span>}
                  </Link>
                </div>
              )}
            </div>
            )}
          </div>
        )}

        {modaler}
      </nav>

      {etterMeny}
    </>
  );
}

export default NavBar;
